import React, { useState, useEffect } from 'react';
import { Sparkles, TrendingUp, Users } from 'lucide-react';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';
import { supabase } from '../../lib/supabaseClient';
import WorkdayCommunityPosts from '../../components/workday-client/WorkdayCommunityPosts';
import CommunityPost from '../../components/consultant/CommunityPost';

export default function WorkdayCommunity() {
  const { signOut } = useAuth();
  const { theme, toggleTheme } = useWorkdayClientTheme();
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [posts, setPosts] = useState<any[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'trending'>('all');

  useEffect(() => {
    fetchLogo();
    fetchConsultantPosts();
  }, []);

  useEffect(() => {
    fetchConsultantPosts();
  }, [filter]);

  const fetchLogo = async () => {
    try {
      setLogoLoading(true);

      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'logo_url')
          .single();

        if (error) {
          setLogoLoading(false);
          return;
        }

        if (data?.value) {
          const img = new Image();
          img.onload = () => {
            setLogoUrl(data.value);
            setLogoLoading(false);
          };
          img.onerror = () => {
            setLogoLoading(false);
          };
          img.src = data.value;
        } else {
          setLogoLoading(false);
        }
      } catch {
        setLogoLoading(false);
      }
    } catch {
      setLogoLoading(false);
    }
  };

  const fetchConsultantPosts = async () => {
    try {
      setPostsLoading(true);
      let query = supabase
        .from('consultant_community_posts')
        .select('*')
        .eq('visibility', 'public')
        .order('created_at', { ascending: false });

      if (filter === 'trending') {
        query = query.order('likes_count', { ascending: false });
      }

      const { data, error } = await query;
      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching consultant posts:', error);
    } finally {
      setPostsLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/customer/login');
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark'
        ? 'bg-black text-white'
        : 'bg-gray-50 text-gray-900'
    }`}>
      <WorkdayClientHeader />

      <main className="pt-32 px-6 pb-6 max-w-7xl mx-auto">
        <WorkdayCommunityPosts />

        <div className="mt-12">
          <div className="mb-6">
            <h2 className={`text-2xl font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Consultant Community Posts
            </h2>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Connect and follow Workday consultants sharing insights and updates
            </p>
          </div>

          <div className={`flex items-center space-x-2 mb-6 p-1 rounded-lg ${
            theme === 'dark' ? 'bg-gray-900' : 'bg-white'
          }`}>
            <button
              onClick={() => setFilter('all')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                filter === 'all'
                  ? 'bg-[#40E0D0] text-white'
                  : theme === 'dark'
                  ? 'text-gray-400 hover:bg-gray-800'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">All Posts</span>
            </button>
            <button
              onClick={() => setFilter('trending')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                filter === 'trending'
                  ? 'bg-[#40E0D0] text-white'
                  : theme === 'dark'
                  ? 'text-gray-400 hover:bg-gray-800'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              <span className="font-medium">Trending</span>
            </button>
          </div>

          {postsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#40E0D0]"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className={`text-center py-12 rounded-xl ${
              theme === 'dark' ? 'bg-gray-900' : 'bg-white'
            }`}>
              <Users className={`w-16 h-16 mx-auto mb-4 ${
                theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
              }`} />
              <h3 className={`text-xl font-semibold mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                No public posts yet
              </h3>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                Check back soon for updates from our consultant community!
              </p>
            </div>
          ) : (
            <div className="max-w-2xl">
              {posts.map((post) => (
                <CommunityPost
                  key={post.id}
                  post={post}
                  onDelete={() => {}}
                  onUpdate={fetchConsultantPosts}
                  theme={theme}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
